/**
 * Lead Origin Tracker - JavaScript Utility
 * Sistema de rastreamento de origem de leads com suporte a:
 * - UTM parameters (prioridade máxima) - source e campaign
 * - Ad parameters (gclid, fbclid, etc) para capturar anúncios sem UTM
 * - Referrer detection (redes sociais, buscadores)
 * - Last-touch attribution (sempre atualiza com origem mais recente)
 */

const STORAGE_KEY = "lead_origin";

/**
 * Verifica UTM source e campaign na URL
 */
function checkUTMParameters() {
  if (typeof window === "undefined") return null;

  const urlParams = new URLSearchParams(window.location.search);
  if (urlParams.has("utm_source")) {
    const source = urlParams.get("utm_source").toLowerCase();
    const campaign = urlParams.get("utm_campaign")?.toLowerCase() || undefined;
    return { source, campaign };
  }
  return null;
}

/**
 * Detecta parâmetros de anúncios quando não há UTM
 * Captura clicks de ads mesmo sem UTM configurado
 *
 * Parâmetros detectados:
 * - gclid: Google Ads
 * - fbclid: Meta Ads (Facebook/Instagram)
 * - li_fat_id: LinkedIn Ads
 * - ttclid: TikTok Ads
 * - msclkid: Microsoft/Bing Ads
 *
 * Retorna também o gad_campaignid se for Google Ads
 */
function detectAdParameters() {
  if (typeof window === "undefined") return null;

  const urlParams = new URLSearchParams(window.location.search);
  const referrer = typeof document !== "undefined" ? document.referrer : "";

  // Google Ads (gclid) - também captura gad_campaignid
  if (urlParams.has("gclid")) {
    const googleAdsCampaignId = urlParams.get("gad_campaignid") || undefined;
    return {
      source: "google-ads",
      googleAdsCampaignId,
    };
  }

  // Meta Ads (fbclid) - diferencia Facebook/Instagram pelo referrer
  if (urlParams.has("fbclid")) {
    if (referrer) {
      try {
        const hostname = new URL(referrer).hostname.toLowerCase();
        if (hostname.includes("instagram")) {
          return { source: "instagram-ads" };
        }
      } catch (e) {
        // Se falhar ao parsear referrer, continua como facebook-ads
      }
    }
    return { source: "facebook-ads" };
  }

  // LinkedIn Ads
  if (urlParams.has("li_fat_id")) {
    return { source: "linkedin-ads" };
  }

  return null;
}

/**
 * Identifica origem pelo referrer
 */
function identifyFromReferrer(referrer) {
  try {
    const hostname = new URL(referrer).hostname.toLowerCase();

    // Buscadores
    if (hostname.includes("google")) return "google";
    if (hostname.includes("bing")) return "bing";
    if (hostname.includes("yahoo")) return "yahoo";
    if (hostname.includes("duckduckgo")) return "duckduckgo";

    // Redes Sociais
    if (hostname.includes("facebook") || hostname.includes("fb.com"))
      return "facebook";
    if (hostname.includes("instagram")) return "instagram";
    if (hostname.includes("linkedin") || hostname.includes("lnkd.in"))
      return "linkedin";
    if (
      hostname.includes("twitter") ||
      hostname.includes("x.com") ||
      hostname.includes("t.co")
    )
      return "twitter";
    if (hostname.includes("youtube") || hostname.includes("youtu.be"))
      return "youtube";
    if (hostname.includes("tiktok")) return "tiktok";
    if (hostname.includes("pinterest")) return "pinterest";
    if (hostname.includes("reddit")) return "reddit";
    if (hostname.includes("whatsapp")) return "whatsapp";

    // Outros sites (retorna domínio limpo)
    return hostname.replace("www.", "");
  } catch (e) {
    return "unknown";
  }
}

/**
 * Verifica se é navegação interna (dentro do próprio site)
 */
function isInternalNavigation(referrer) {
  if (typeof window === "undefined") return false;

  try {
    const referrerHost = new URL(referrer).hostname;
    const currentHost = window.location.hostname;
    return referrerHost === currentHost;
  } catch (e) {
    return false;
  }
}

/**
 * Salva origem no localStorage
 */
function save(source, campaign, googleAdsCampaignId) {
  try {
    const data = {
      source,
      campaign,
      googleAdsCampaignId,
      timestamp: Date.now(),
    };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
  } catch (e) {
    console.warn("LeadOrigin: Não foi possível salvar no localStorage", e);
  }
}

/**
 * Recupera origem salva como string formatada
 */
function get() {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (!stored) return null;

    // Tenta parsear como JSON (novo formato)
    try {
      const data = JSON.parse(stored);
      return formatOriginString(data.source, data.campaign);
    } catch {
      // Se não for JSON, é formato antigo (string simples) - mantém compatibilidade
      return stored;
    }
  } catch (e) {
    return null;
  }
}

/**
 * Formata origem como string "source | campaign" ou apenas "source"
 */
function formatOriginString(source, campaign) {
  if (campaign) {
    return `${source} | ${campaign}`;
  }
  return source;
}

/**
 * Limpa origem salva
 */
export function clearLeadOrigin() {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch (e) {
    console.warn("LeadOrigin: Não foi possível limpar localStorage", e);
  }
}

/**
 * Captura origem do lead
 * Prioridade: UTM > Ad Parameters > Referrer Externo > Direct
 */
export function captureLeadOrigin() {
  if (typeof window === "undefined") return null;

  const referrer = typeof document !== "undefined" ? document.referrer : "";

  // Se veio do próprio site (navegação interna), mantém origem anterior
  if (referrer && isInternalNavigation(referrer)) {
    return get();
  }

  // Prioridade 1: UTM parameters (source + campaign)
  const utmParams = checkUTMParameters();
  if (utmParams) {
    save(utmParams.source, utmParams.campaign);
    return formatOriginString(utmParams.source, utmParams.campaign);
  }

  // Prioridade 2: Parâmetros de anúncios (gclid, fbclid, etc)
  const adOrigin = detectAdParameters();
  if (adOrigin) {
    save(adOrigin.source, undefined, adOrigin.googleAdsCampaignId);
    return adOrigin.source;
  }

  // Prioridade 3: Referrer externo
  if (referrer && referrer !== "") {
    const origin = identifyFromReferrer(referrer);
    save(origin);
    return origin;
  }

  // Prioridade 4: Direct (só salva se não existir origem anterior)
  const existing = get();
  if (!existing) {
    save("Probies | direct");
    return "Probies | direct";
  }

  return existing;
}

/**
 * Retorna origem salva (sem capturar novamente)
 */
export function getLeadOrigin() {
  return get() || "Probies | direct";
}

/**
 * Retorna dados de origem separados (source, campaign e googleAdsCampaignId)
 */
export function getLeadOriginData() {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (!stored)
      return {
        source: "Probies | direct",
        campaign: "",
        googleAdsCampaignId: null,
      };

    // Tenta parsear como JSON (novo formato)
    const data = JSON.parse(stored);
    return {
      source: data.source,
      campaign: data.campaign || "",
      googleAdsCampaignId: data.googleAdsCampaignId || null,
    };
  } catch (e) {
    return { source: "Probies | direct", campaign: "", googleAdsCampaignId: null };
  }
}
